<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div>
        <h1 class="text-lg font-semibold text-gray-900">Gestion des utilisateurs</h1>
        <p class="text-xs text-gray-400">{{ total }} utilisateur(s) au total</p>
      </div>
      <button
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
        @click="showModal('add')">
        <i class="fas fa-plus text-[10px]"></i>
        Nouvel utilisateur
      </button>
    </div>

    <!-- Filtres -->
    <div class="bg-white rounded-xl border border-gray-200 p-4 mb-5">
      <div class="flex flex-wrap gap-3 items-center">
        <div class="relative flex-1 min-w-[220px]">
          <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
          <input type="text" v-model="search" placeholder="Rechercher un utilisateur..."
            class="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            @input="handleSearch" />
        </div>
        <select v-model="filters.role" @change="loadUsers"
          class="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors min-w-[160px]">
          <option value="">Tous les rôles</option>
          <option value="admin">Administrateur</option>
          <option value="user">Utilisateur standard</option>
        </select>
        <select v-model="filters.active" @change="loadUsers"
          class="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors min-w-[140px]">
          <option value="">Tous les statuts</option>
          <option value="true">Actif</option>
          <option value="false">Inactif</option>
        </select>
      </div>
    </div>

    <!-- Tableau -->
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <!-- Chargement -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
        <p class="text-sm text-gray-400">Chargement des utilisateurs...</p>
      </div>

      <!-- État vide -->
      <div v-else-if="users.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
        <div class="text-gray-300 mb-3">
          <i class="fas fa-users text-3xl"></i>
        </div>
        <p class="text-sm font-medium text-gray-500 mb-1">Aucun utilisateur trouvé</p>
        <p class="text-xs text-gray-400 mb-4">Cliquez sur "Nouvel utilisateur" pour ajouter un utilisateur au système.</p>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          @click="showModal('add')">
          <i class="fas fa-plus text-[10px]"></i>
          Ajouter un utilisateur
        </button>
      </div>

      <!-- Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[900px]">
          <thead class="bg-gray-50/80">
            <tr>
              <th class="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Nom</th>
              <th class="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Email</th>
              <th class="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Rôle</th>
              <th class="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-20">Statut</th>
              <th class="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Date d'ajout</th>
              <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-32">Ventes</th>
              <th class="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-36">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr v-for="user in users" :key="user.id"
              class="hover:bg-gray-50/60 transition-colors"
              :class="{ 'opacity-50': !user.active }">
              <td class="px-5 py-3">
                <router-link :to="{ name: 'user-detail', params: { id: user.id } }" class="flex items-center gap-2.5 group">
                  <div class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span class="text-[11px] font-bold text-primary">{{ getInitials(user.name) }}</span>
                  </div>
                  <span class="text-sm font-medium text-gray-900 group-hover:text-primary transition-colors">{{ user.name }}</span>
                </router-link>
              </td>
              <td class="px-3 py-3">
                <span class="text-sm text-gray-500">{{ user.email }}</span>
              </td>
              <td class="px-3 py-3 text-center">
                <span :class="[
                  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold',
                  user.role === 'admin' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500'
                ]">
                  {{ user.role === 'admin' ? 'Administrateur' : 'Utilisateur' }}
                </span>
              </td>
              <td class="px-3 py-3 text-center">
                <span :class="[
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold',
                  user.active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'
                ]">
                  <span :class="['w-1.5 h-1.5 rounded-full', user.active ? 'bg-green-500' : 'bg-gray-400']"></span>
                  {{ user.active ? 'Actif' : 'Inactif' }}
                </span>
              </td>
              <td class="px-3 py-3">
                <span class="text-xs text-gray-500">{{ formatDate(user.created_at) }}</span>
              </td>
              <td class="px-3 py-3 text-right">
                <span v-if="user.statistics" class="text-xs text-gray-500">
                  {{ user.statistics.sales_count }} ({{ formatPrice(user.statistics.sales_total) }})
                </span>
                <span v-else class="text-xs text-gray-300">-</span>
              </td>
              <td class="px-3 py-3 text-center">
                <div class="flex items-center justify-center gap-1">
                  <button
                    class="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                    @click="router.push({ name: 'user-detail', params: { id: user.id } })" title="Voir les détails">
                    <i class="fas fa-eye text-[10px]"></i>
                  </button>
                  <button
                    class="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                    @click="showModal('edit', user)" title="Modifier">
                    <i class="fas fa-pen text-[10px]"></i>
                  </button>
                  <button
                    :class="[
                      'w-7 h-7 flex items-center justify-center rounded-lg transition-colors',
                      user.active
                        ? 'text-gray-300 hover:text-amber-500 hover:bg-amber-50'
                        : 'text-gray-300 hover:text-green-500 hover:bg-green-50'
                    ]"
                    @click="showModal('status', user)" :title="user.active ? 'Désactiver' : 'Activer'">
                    <i :class="['text-[10px]', user.active ? 'fas fa-power-off' : 'fas fa-check-circle']"></i>
                  </button>
                  <button
                    class="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    @click="showModal('delete', user)"
                    :disabled="user.active && user.statistics && user.statistics.sales_count > 0"
                    title="Supprimer">
                    <i class="fas fa-trash text-[10px]"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <PaginationComponent
        v-if="users.length > 0"
        :current-page="currentPage"
        :per-page="perPage"
        :total="total"
        :to="paginationTo"
        :from="paginationFrom"
        @page-change="goToPage"
      />
    </div>

    <!-- Modal formulaire utilisateur -->
    <transition name="modal">
      <div v-if="showUserFormModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="closeModal('form')">
        <div class="bg-white rounded-xl max-w-lg w-full shadow-xl" @click.stop>
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 class="text-base font-semibold text-gray-900">{{ isEditMode ? 'Modifier un utilisateur' : 'Ajouter un utilisateur' }}</h3>
            <button class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-500 border border-gray-200 rounded-lg transition-colors"
              @click="closeModal('form')">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>
          <form @submit.prevent="saveUser" class="p-6 space-y-4">
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Nom complet <span class="text-red-500">*</span></p>
              <input type="text" v-model="userForm.name" required
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Email <span class="text-red-500">*</span></p>
              <input type="email" v-model="userForm.email" required
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Rôle <span class="text-red-500">*</span></p>
              <select v-model="userForm.role" required
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors">
                <option value="admin">Administrateur</option>
                <option value="user">Utilisateur standard</option>
              </select>
            </div>
            <div v-if="isEditMode" class="flex items-center gap-3">
              <input type="checkbox" id="active" v-model="userForm.active"
                class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary/20" />
              <label for="active" class="text-sm text-gray-700 cursor-pointer">{{ userForm.active ? 'Actif' : 'Inactif' }}</label>
            </div>
            <div v-if="!isEditMode" class="flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg">
              <i class="fas fa-info-circle text-blue-500 text-xs flex-shrink-0"></i>
              <span class="text-xs text-blue-700">Un email avec un lien pour définir le mot de passe sera envoyé à l'utilisateur</span>
            </div>
            <div class="flex gap-3 pt-2">
              <button type="button"
                class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="closeModal('form')">
                Annuler
              </button>
              <button type="submit"
                class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="saving">
                <i v-if="saving" class="fas fa-spinner fa-spin text-[10px]"></i>
                <span v-else>{{ isEditMode ? 'Enregistrer' : 'Ajouter' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Modal de confirmation -->
    <transition name="modal">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="closeModal('confirm')">
        <div class="bg-white rounded-xl max-w-md w-full shadow-xl" @click.stop>
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 class="text-base font-semibold text-gray-900">{{ confirmTitle }}</h3>
            <button class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-500 border border-gray-200 rounded-lg transition-colors"
              @click="closeModal('confirm')">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-500 mb-5">{{ confirmMessage }}</p>
            <div class="flex gap-3">
              <button
                class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="closeModal('confirm')">
                Annuler
              </button>
              <button
                class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                @click="confirmAction" :disabled="saving">
                <i v-if="saving" class="fas fa-spinner fa-spin text-[10px]"></i>
                <span v-else>{{ confirmButtonText }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import usersService from '@/services/usersService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'

export default defineComponent({
  name: 'UsersListView',
  components: {
    PaginationComponent
  },
  setup() {
    const router = useRouter();
    const users = ref([]);
    const loading = ref(true);
    const saving = ref(false);
    const search = ref('');
    const filters = reactive({
      role: '',
      active: ''
    });

    const currentPage = ref(1);
    const lastPage = ref(1);
    const perPage = ref(10);
    const total = ref(0);
    const paginationFrom = ref(0);
    const paginationTo = ref(0);
    const waitSearch = ref(null);

    const showUserFormModal = ref(false);
    const showConfirmModal = ref(false);
    const isEditMode = ref(false);
    const currentUser = ref(null);
    const userForm = reactive({
      id: null,
      name: '',
      email: '',
      role: 'user',
      active: true
    });
    const confirmAction = ref(null);
    const confirmTitle = ref('');
    const confirmMessage = ref('');
    const confirmButtonText = ref('');
    const confirmButtonClass = ref('');

    const loadUsers = async () => {
      try {
        loading.value = true;

        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          sort_by: 'created_at',
          sort_direction: 'desc'
        };

        if (search.value) {
          params.search = search.value;
        }
        if (filters.role) {
          params.role = filters.role;
        }
        if (filters.active !== '') {
          params.active = filters.active === 'true';
        }

        const response = await usersService.getUsers(params);
        users.value = response.data.data || [];

        if (response.data.meta) {
          const meta = response.data.meta;
          total.value = meta.total || 0;
          perPage.value = meta.per_page || 10;
          paginationFrom.value = meta.from || 0;
          paginationTo.value = meta.to || 0;
          lastPage.value = Math.ceil(total.value / perPage.value);
        }

      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      waitSearch.value = setTimeout(() => {
        currentPage.value = 1;
        loadUsers();
      }, 500);
    };

    const goToPage = (page) => {
      if (page < 1 || page > lastPage.value) return;
      currentPage.value = page;
      loadUsers();
    };

    const showModal = (type, user = null) => {
      currentUser.value = user;

      if (type === 'add' || type === 'edit') {
        isEditMode.value = type === 'edit';

        if (isEditMode.value && user) {
          userForm.id = user.id;
          userForm.name = user.name;
          userForm.email = user.email;
          userForm.role = user.role;
          userForm.active = user.active;
        } else {
          userForm.id = null;
          userForm.name = '';
          userForm.email = '';
          userForm.role = 'user';
          userForm.active = true;
        }

        showUserFormModal.value = true;
      } else if (type === 'delete' && user) {
        showConfirmModal.value = true;
        confirmTitle.value = 'Supprimer l\'utilisateur';
        confirmMessage.value = `Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.name} ?`;
        confirmButtonText.value = 'Supprimer';
        confirmButtonClass.value = 'delete-confirm-btn';
        confirmAction.value = deleteUser;
      } else if (type === 'status' && user) {
        showConfirmModal.value = true;
        const action = user.active ? 'désactiver' : 'activer';
        confirmTitle.value = user.active ? 'Désactiver l\'utilisateur' : 'Activer l\'utilisateur';
        confirmMessage.value = `Êtes-vous sûr de vouloir ${action} l'utilisateur ${user.name} ?`;
        confirmButtonText.value = user.active ? 'Désactiver' : 'Activer';
        confirmButtonClass.value = user.active ? 'deactivate-confirm-btn' : 'activate-confirm-btn';
        confirmAction.value = toggleUserStatus;
      }
    };

    const closeModal = (type) => {
      if (type === 'form') {
        showUserFormModal.value = false;
      } else if (type === 'confirm') {
        showConfirmModal.value = false;
      }
    };

    const saveUser = async () => {
      try {
        saving.value = true;

        if (isEditMode.value) {
          await usersService.updateUser(userForm.id, {
            name: userForm.name,
            email: userForm.email,
            role: userForm.role,
            active: userForm.active
          });
        } else {
          await usersService.createUser({
            name: userForm.name,
            email: userForm.email,
            role: userForm.role
          });
        }

        closeModal('form');
        loadUsers();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de l\'utilisateur:', error);
      } finally {
        saving.value = false;
      }
    };

    const deleteUser = async () => {
      if (!currentUser.value) return;

      try {
        saving.value = true;
        await usersService.deleteUser(currentUser.value.id);
        closeModal('confirm');
        loadUsers();
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      } finally {
        saving.value = false;
      }
    };

    const toggleUserStatus = async () => {
      if (!currentUser.value) return;

      try {
        saving.value = true;
        await usersService.toggleUserStatus(currentUser.value.id, !currentUser.value.active);
        closeModal('confirm');
        loadUsers();
      } catch (error) {
        console.error('Erreur lors du changement de statut de l\'utilisateur:', error);
      } finally {
        saving.value = false;
      }
    };

    const getInitials = (name) => {
      if (!name) return '';
      return name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    const formatPrice = (amount) => {
      if (amount === undefined || amount === null) return '0 XOF';

      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount);
    };

    onMounted(() => {
      loadUsers();
    });

    return {
      router,
      users,
      loading,
      saving,
      search,
      filters,
      currentPage,
      lastPage,
      perPage,
      total,
      paginationFrom,
      paginationTo,
      userForm,
      isEditMode,
      showUserFormModal,
      showConfirmModal,
      confirmTitle,
      confirmMessage,
      confirmButtonText,
      confirmButtonClass,
      loadUsers,
      handleSearch,
      goToPage,
      showModal,
      closeModal,
      saveUser,
      confirmAction,
      getInitials,
      formatDate,
      formatPrice
    };
  }
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
